import { css, keyframes } from "@emotion/react";
import { LegacyPortalDashboardSalesAggregationUnit } from "@spwn-portal/grpc";

import type { TicketSalesByOrderDate } from "./useGetTicketSalesByOrderDate";

type Enumerate<
  N extends number,
  Acc extends number[] = [],
> = Acc["length"] extends N
  ? Acc[number]
  : Enumerate<N, [...Acc, Acc["length"]]>;

// TypeScriptは剰余計算の結果がCHART_COLORSの有効な添字になることまでは推論できないため、参照可能な添字を型で明示する。
type ChartColorIndex = Enumerate<(typeof CHART_COLORS)["length"]>;

const CHART_COLORS = [
  "#00c2ae",
  "#5aa9c9",
  "#9083c9",
  "#63c3a6",
  "#e0a54e",
  "#e8836b",
  "#e277c8",
  "#d4c24a",
  "#d65f7a",
  "#6f9fd4",
  "#8a7de0",
  "#c9a978",
] as const;

export const getChartColor = (index: number): string => {
  const chartColorIndex = (index % CHART_COLORS.length) as ChartColorIndex;
  return CHART_COLORS[chartColorIndex];
};

const orderDateFormatter = new Intl.DateTimeFormat("en-CA", {
  day: "2-digit",
  hour: "2-digit",
  hourCycle: "h23",
  minute: "2-digit",
  month: "2-digit",
  timeZone: "Asia/Tokyo",
});

const aggregationUnitDisplays = {
  [LegacyPortalDashboardSalesAggregationUnit.UNSPECIFIED]: {
    aggregationLabel: "日別",
    orderDateLabel: "支払日",
  },
  [LegacyPortalDashboardSalesAggregationUnit.DAY]: {
    aggregationLabel: "日別",
    orderDateLabel: "支払日",
  },
  [LegacyPortalDashboardSalesAggregationUnit.HOUR]: {
    aggregationLabel: "時間別",
    orderDateLabel: "支払日時",
  },
  [LegacyPortalDashboardSalesAggregationUnit.MINUTE]: {
    aggregationLabel: "分別",
    orderDateLabel: "支払日時",
  },
} satisfies Record<
  LegacyPortalDashboardSalesAggregationUnit,
  {
    aggregationLabel: string;
    orderDateLabel: string;
  }
>;

export const getSalesDashboardChartDisplay = (
  aggregationUnit: LegacyPortalDashboardSalesAggregationUnit
) =>
  aggregationUnitDisplays[aggregationUnit] ??
  aggregationUnitDisplays[
    LegacyPortalDashboardSalesAggregationUnit.UNSPECIFIED
  ];

export const formatSalesDashboardOrderDate = (
  orderDateMilliseconds: number,
  aggregationUnit: LegacyPortalDashboardSalesAggregationUnit
): string => {
  const parts = orderDateFormatter.formatToParts(
    new Date(orderDateMilliseconds)
  );
  const getPartValue = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((part) => part.type === type)?.value ?? "";
  const date = `${getPartValue("month")}/${getPartValue("day")}`;

  if (aggregationUnit === LegacyPortalDashboardSalesAggregationUnit.HOUR) {
    return `${date} ${getPartValue("hour")}時`;
  }

  if (aggregationUnit === LegacyPortalDashboardSalesAggregationUnit.MINUTE) {
    return `${date} ${getPartValue("hour")}:${getPartValue("minute")}`;
  }

  return date;
};

export const chartMargin = {
  bottom: 24,
  left: 16,
  right: 32,
  top: 16,
};

export const salesDashboardChartTooltipWrapperStyle = {
  zIndex: 1,
};

export type ChartRow = {
  label: string;
} & Record<string, number | string>;

export const createEmptyChartRow = (
  orderDateMilliseconds: number,
  ticketNames: string[],
  aggregationUnit: LegacyPortalDashboardSalesAggregationUnit
): ChartRow => {
  const row: ChartRow = {
    label: formatSalesDashboardOrderDate(
      orderDateMilliseconds,
      aggregationUnit
    ),
  };

  ticketNames.forEach((ticketName) => {
    row[ticketName] = 0;
  });

  return row;
};

export const createTicketNames = (
  ticketSalesByOrderDate: TicketSalesByOrderDate[]
): string[] =>
  Array.from(
    new Set(ticketSalesByOrderDate.map(({ ticketName }) => ticketName))
  );

export const commonStyles = {
  skeleton: css`
    position: relative;
    display: block;
    height: 100%;
    overflow: hidden;
    background: #263544;
    border-radius: 8px;
    &::after {
      content: "";
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      background: linear-gradient(
        90deg,
        rgb(255 255 255 / 0%) 0%,
        rgb(255 255 255 / 10%) 50%,
        rgb(255 255 255 / 0%) 100%
      );
      animation: ${keyframes`
        100% {
        transform: translateX(100%) ;
        }
        `}
        1400ms ease-in-out infinite;
      transform: translateX(-100%);
    }
    @media (prefers-reduced-motion: reduce) {
      &::after {
        animation: none;
        transform: translateX(0);
      }
    }
  `,
  section: css`
    width: 100%;
    padding: clamp(16px, 2.5vw, 24px);
    background: #1b2734;
    border: 1px solid #2e3d4c;
    border-radius: 10px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.22);
  `,
  header: css`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 5px;
    margin-bottom: 14px;
  `,
  title: css`
    margin: 0;
    color: #ffffff;
    font-size: 14.5px;
    font-weight: 700;
    letter-spacing: 0.4px;
  `,
  subtitle: css`
    margin: 0;
    color: #8b96a2;
    font-size: 12px;
    letter-spacing: 0.2px;
  `,
  chartArea: css`
    position: relative;
    width: 100%;
    height: 294px;
  `,
  notDataText: css`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    color: #fff;
  `,
  legend: css`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px;
    padding: 0;
    margin: 0 0 10px;
    list-style: none;
  `,
  legendItem: css`
    display: flex;
    align-items: center;
    gap: 6px;
    color: #b9c3cd;
    font-size: 12.5px;
  `,
};
